import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useTheme } from '../../context/ThemeContext';
import {
  Mail,
  Send,
  Users,
  Filter,
  CheckSquare,
  Square,
  Loader2,
  AlertCircle,
  CheckCircle,
  Flame,
  Tag,
  Download,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BulkEmail() {
  const requestVersion = useRef(0);
  const [leads, setLeads] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    service: '',
    hotOnly: false
  });
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [emailData, setEmailData] = useState({
    subject: '',
    body: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [reviewNeeded, setReviewNeeded] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ by_status: {}, by_service: {} });
  const { theme } = useTheme();

  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const inputBg = theme === 'dark' ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900';

  const loadEmailList = useCallback(async ({ background = false } = {}) => {
    const version = ++requestVersion.current;
    if (!background) setLoading(true);
    setRefreshing(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');

      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.service) params.set('service', filters.service);
      if (filters.hotOnly) params.set('hot_only', 'true');
      const url = `${API_URL}/api/clinic/${clinicId}/follow-ups/email-list?${params}`;

      const res = await fetch(url, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        if (version !== requestVersion.current) return;
        if (!background) setMessage({ type: "", text: "" });
        setLeads(data.leads || []);
        const currentIds = new Set((data.leads || []).map(lead => lead.id));
        setSelectedIds(previous => previous.filter(id => currentIds.has(id)));
        setLastUpdated(new Date());
        setCoverage(data.coverage || null);
        setReviewNeeded(data.review_needed || []);
        setStats({ by_status: data.by_status || {}, by_service: data.by_service || {} });
        setAvailableStatuses(data.statuses || []);
        setAvailableServices(data.services || []);
      } else {
        throw new Error('No se pudo cargar la lista de correos');
      }
    } catch (err) {
      if (version !== requestVersion.current) return;
      setLeads([]);
      setSelectedIds([]);
      setCoverage(null);
      setReviewNeeded([]);
      setLastUpdated(null);
      setMessage({ type: 'error', text: 'No se pudo actualizar la lista. Reintenta.' });
    }
    if (version === requestVersion.current) {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters]);

  useEffect(() => {
    loadEmailList();
    return () => { requestVersion.current += 1; };
  }, [loadEmailList]);
  useAutoRefresh(loadEmailList, sending);

  const exportCsv = (review = false) => {
    const rows = review ? reviewNeeded : (selectedIds.length ? leads.filter(lead => selectedIds.includes(lead.id)) : leads);
    const cell = value => {
      let text = String(value ?? '');
      if (/^[=+@\-\t\r\n]/.test(text.trimStart()) || /^[\t\r\n]/.test(text)) text = "'" + text;
      return '"' + text.replace(/"/g, '""') + '"';
    };
    const data = [['email', 'nombre', 'telefono', 'estado', 'origen', 'consentimiento_marketing', 'revision'],
      ...rows.map(lead => [lead.lead_email, lead.lead_name, lead.lead_phone, lead.status,
        (lead.sources || []).join('; '), lead.marketing_consent || 'unknown', lead.review_reason || ''])];
    const url = URL.createObjectURL(new Blob(['\uFEFF' + data.map(row => row.map(cell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8;' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = review ? 'AIMP_correos_por_revisar.csv' : 'AIMP_lista_correos.csv';
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === leads.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(leads.map(l => l.id));
    }
  };

  const sendEmails = async () => {
    if (!emailData.subject.trim() || !emailData.body.trim()) {
      setMessage({ type: 'error', text: 'Debes ingresar asunto y mensaje' });
      return;
    }

    if (selectedIds.length === 0) {
      setMessage({ type: 'error', text: 'No hay leads seleccionados' });
      return;
    }

    setSending(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');

      const res = await fetch(`${API_URL}/api/clinic/${clinicId}/follow-ups/send-bulk-email`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_ids: selectedIds.length > 0 ? selectedIds : [],
          subject: emailData.subject,
          body: emailData.body,
          filter_status: selectedIds.length === 0 ? filters.status : null,
          filter_service: selectedIds.length === 0 ? filters.service : null,
          filter_hot_only: selectedIds.length === 0 ? filters.hotOnly : false
        })
      });

      const data = await res.json();

      if (data.success) {
        setMessage({ type: 'success', text: `✅ ${data.sent} correos enviados exitosamente` });
        setSelectedIds([]);
        setEmailData({ subject: '', body: '' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Error al enviar correos' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error al enviar correos' });
    }
    setSending(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const statusLabels = {
    'recovered': 'Recuperado de conversación',
    'new': 'Nuevo',
    'follow_up_1': 'Follow-up 1',
    'follow_up_2': 'Follow-up 2',
    'responded': 'Respondió',
    'no_response': 'Sin respuesta',
    'opted_out': 'Opt-out'
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-2 ${textPrimary}`}>
            <Mail className="w-7 h-7 text-blue-500" />
            Email Masivo
          </h1>
          <p className={textSecondary}>Lista unificada de leads y correos encontrados en conversaciones. Sin duplicados.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button onClick={loadEmailList} disabled={refreshing || sending} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Actualizar correos</Button>
            <Button onClick={() => exportCsv()} disabled={loading || !leads.length} variant="outline"><Download className="w-4 h-4 mr-2" />Exportar CSV {selectedIds.length ? `(${selectedIds.length})` : `(${leads.length})`}</Button>
            {reviewNeeded.length > 0 && <Button onClick={() => exportCsv(true)} disabled={loading} variant="outline">Exportar revisión ({reviewNeeded.length})</Button>}
          </div>
          {coverage && <p className={`text-sm mt-3 ${textSecondary}`}>
            {coverage.crm_leads} leads revisados · {coverage.recovered_from_conversations} correos adicionales recuperados de conversaciones · {coverage.suppressed} correos excluidos por baja · {coverage.review_needed || 0} direcciones por revisar.
          </p>}
          <p className={`text-sm mt-2 ${textSecondary}`} role="status">La lista se actualiza al abrir o volver a esta pestaña y cada 5 minutos mientras esté visible.{lastUpdated ? ` Última actualización: ${lastUpdated.toLocaleTimeString('es-MX')}.` : ''}</p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Lead List */}
          <div className="lg:col-span-1">
            <Card className={cardBg}>
              <CardHeader>
                <CardTitle className={`text-lg flex items-center justify-between ${textPrimary}`}>
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Leads con Email ({leads.length})
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAll}
                    className={textMuted}
                  >
                    {selectedIds.length === leads.length && leads.length > 0 ? 'Deseleccionar' : 'Seleccionar todos'}
                  </Button>
                </CardTitle>
                
                {/* Filters */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      className={`flex-1 px-2 py-1 rounded text-sm ${inputBg}`}
                    >
                      <option value="">Todos los estados</option>
                      {availableStatuses.map(s => (
                        <option key={s} value={s}>{statusLabels[s] || s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-gray-400" />
                    <select
                      value={filters.service}
                      onChange={(e) => setFilters({ ...filters, service: e.target.value })}
                      className={`flex-1 px-2 py-1 rounded text-sm ${inputBg}`}
                    >
                      <option value="">Todos los servicios</option>
                      {availableServices.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.hotOnly}
                      onChange={(e) => setFilters({ ...filters, hotOnly: e.target.checked })}
                      className="rounded"
                    />
                    <Flame className="w-4 h-4 text-orange-500" />
                    <span className={`text-sm ${textSecondary}`}>Solo Hot Leads</span>
                  </label>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : leads.length > 0 ? (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {leads.map((lead) => (
                      <div
                        key={lead.id}
                        onClick={() => toggleSelect(lead.id)}
                        className={`p-3 rounded-lg cursor-pointer border transition-all ${
                          selectedIds.includes(lead.id)
                            ? 'border-blue-500 bg-blue-500/10'
                            : theme === 'dark' ? 'border-slate-600 hover:border-slate-500' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {selectedIds.includes(lead.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`font-medium truncate ${textPrimary}`}>{lead.lead_name}</p>
                              {lead.is_hot_lead && <span>🔥</span>}
                            </div>
                            <p className={`text-sm truncate ${textMuted}`}>{lead.lead_email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'
                              } ${textMuted}`}>
                                {statusLabels[lead.status] || lead.status}
                              </span>
                              {lead.interested_service && (
                                <span className={`text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400`}>
                                  {lead.interested_service}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`${textMuted} text-center py-8`}>
                    No hay leads con email
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Email Composer */}
          <div className="lg:col-span-2">
            <Card className={cardBg}>
              <CardHeader>
                <CardTitle className={`text-lg flex items-center gap-2 ${textPrimary}`}>
                  <Send className="w-5 h-5 text-green-500" />
                  Componer Email
                </CardTitle>
                <CardDescription className={textSecondary}>
                  {selectedIds.length > 0 
                    ? `${selectedIds.length} leads seleccionados`
                    : 'Selecciona los destinatarios para enviar'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>Asunto</label>
                  <input
                    type="text"
                    value={emailData.subject}
                    onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                    placeholder="Ej: ¡Oferta especial para tu cirugía!"
                    className={`w-full px-4 py-2 rounded-lg border ${inputBg}`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>
                    Mensaje
                    <span className={`ml-2 text-xs ${textMuted}`}>
                      Variables: {'{nombre}'}, {'{servicio}'}
                    </span>
                  </label>
                  <textarea
                    value={emailData.body}
                    onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                    placeholder={`Hola {nombre},

Esperamos que estés muy bien. Queremos recordarte que tenemos una promoción especial en {servicio}.

¿Te gustaría agendar una valoración sin costo?

Saludos,
Dr. César Sánchez
Optall Vision`}
                    rows={10}
                    className={`w-full px-4 py-2 rounded-lg border ${inputBg}`}
                  />
                </div>

                {/* Quick Templates */}
                <div>
                  <p className={`text-sm mb-2 ${textMuted}`}>Plantillas rápidas:</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEmailData({
                        subject: '¡Promoción especial para ti!',
                        body: `Hola {nombre},

Queremos agradecerte tu interés en Optall Vision. 

Tenemos una promoción especial este mes:
✨ 20% de descuento en {servicio}
✨ Valoración sin costo
✨ Financiamiento hasta 12 meses

¿Te gustaría agendar tu cita? Responde a este correo o llámanos.

Saludos cordiales,
Dr. César Sánchez
Optall Vision`
                      })}
                      className={theme === 'dark' ? 'border-slate-600' : ''}
                    >
                      Promoción
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEmailData({
                        subject: 'Recordatorio: Tu valoración te espera',
                        body: `Hola {nombre},

Notamos que mostraste interés en nuestros servicios de {servicio} pero aún no has agendado tu valoración.

Queremos ayudarte a dar el primer paso hacia una mejor visión.

📅 Agenda tu cita hoy y recibe:
- Valoración completamente gratis
- Diagnóstico personalizado
- Plan de tratamiento a tu medida

¿Tienes alguna duda? Estamos para ayudarte.

Saludos,
Equipo Optall Vision`
                      })}
                      className={theme === 'dark' ? 'border-slate-600' : ''}
                    >
                      Recordatorio
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEmailData({
                        subject: 'Información sobre tu consulta',
                        body: `Hola {nombre},

Gracias por contactarnos. Aquí te compartimos información importante sobre {servicio}:

🔹 Somos especialistas en cirugía refractiva con tecnología de punta
🔹 Más de 15 años de experiencia
🔹 Miles de pacientes satisfechos

¿Cuándo te gustaría visitarnos?

Quedamos atentos,
Optall Vision`
                      })}
                      className={theme === 'dark' ? 'border-slate-600' : ''}
                    >
                      Información
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={sendEmails}
                  disabled={loading || sending || !selectedIds.length || (!emailData.subject || !emailData.body)}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Enviar a {selectedIds.length} leads
                </Button>
              </CardContent>
            </Card>

            {/* Stats */}
            <Card className={`${cardBg} mt-4`}>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`text-sm font-medium mb-2 ${textSecondary}`}>Por Estado</p>
                    <div className="space-y-1">
                      {Object.entries(stats.by_status).map(([status, count]) => (
                        <div key={status} className={`flex justify-between text-sm ${textMuted}`}>
                          <span>{statusLabels[status] || status}</span>
                          <span>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className={`text-sm font-medium mb-2 ${textSecondary}`}>Por Servicio</p>
                    <div className="space-y-1">
                      {Object.entries(stats.by_service).map(([service, count]) => (
                        <div key={service} className={`flex justify-between text-sm ${textMuted}`}>
                          <span>{service}</span>
                          <span>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

